<template>
  <div class="w-full">
    <div>
      <AiSpeaker :text="plainAnswer" />
      <div>
        <textarea
          v-if="parsedMarkdown === ''"
          class="w-full min-h-[30rem] resize-none px-4 py-2 dark:bg-gray-700 dark:text-gray-300"
          v-model="userIdeas"
          :placeholder="aiAnswerPlaceHolder"
          @keydown.enter.prevent="onGenerateAiModelAnswer"
        ></textarea>
        <div v-else class="py-2 px-4">
          <span class="dark:text-gray-200">
            <strong> 我的提示: </strong>
            {{ userIdeas }}
          </span>
        </div>
      </div>
      <div v-show="parsedMarkdown !== ''">
        <div v-if="isEditing" class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="relative">
            <div
              class="absolute top-2 left-2 right-14 flex flex-wrap items-center text-gray-500 text-xs sm:text-sm"
            >
              <div class="flex items-center mr-2">
                <TipIcon class="dark:text-gray-200" />
                <span class="hidden sm:inline dark:text-gray-200"
                  >支持 Markdown：</span
                >
              </div>
              <div class="flex flex-wrap gap-1 sm:gap-2">
                <span
                  class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 rounded text-xs whitespace-nowrap"
                >
                  **粗体**
                </span>
                <span
                  class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 rounded text-xs whitespace-nowrap"
                >
                  *斜体*
                </span>
                <span
                  class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 rounded text-xs whitespace-nowrap"
                >
                  # 标题
                </span>
                <span
                  class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 rounded text-xs whitespace-nowrap"
                >
                  - 列表
                </span>
              </div>
            </div>

            <textarea
              v-model="currentAnswer"
              class="w-full min-h-[30rem] resize-none px-4 pt-12 sm:pt-10 pb-2 border rounded dark:bg-gray-700 dark:text-gray-300"
              placeholder="编辑 Markdown 内容..."
            ></textarea>
            <button
              class="text-indigo-600 px-4 py-1 text-sm absolute top-2 right-2 dark:text-indigo-100"
              @click="toggleEditMode"
            >
              退出编辑
            </button>
          </div>
          <div
            v-html="parsedMarkdown"
            class="markdown-content bg-white min-h-[30rem] py-4 px-4 shadow-md overflow-auto dark:bg-gray-700 dark:text-gray-300"
          ></div>
        </div>
        <div v-else class="relative">
          <div
            v-html="parsedMarkdown"
            class="markdown-content bg-white min-h-[30rem] py-4 px-4 shadow-md dark:bg-gray-700 dark:text-gray-300"
          ></div>
          <button
            class="text-indigo-600 px-4 py-1 text- absolute top-2 right-2 dark:text-indigo-100"
            @click="toggleEditMode"
          >
            编辑
          </button>
        </div>
      </div>
      <div class="sticky bottom-0 mt-4 flex justify-end z-10">
        <div v-if="isAuthenticated" class="w-full">
          <LoadingBtn
            v-if="parsedMarkdown === '' || isLoading"
            :text="''"
            :loading-text="'生成中...'"
            :is-loading="isLoading"
            class="text-md text-semibold w-full mt-2"
            :disabled="userIdeas === '' || isLoading"
            :button-class="[
              userIdeas === ''
                ? '!cursor-not-allowed secondary-btn '
                : 'primary-btn',
            ]"
            @click="onGenerateAiModelAnswer"
            style=""
          >
            <template #nonLoadingText>
              <div class="flex items-center">
                AI定制
                <span class="text-sm ms-2">
                  [ 今日剩余
                  <span class="text-red-500">
                    {{ remainingRequests }}
                  </span>
                  次]
                </span>
              </div>
            </template>
          </LoadingBtn>
          <button
            v-else
            :disabled="isLoading"
            :class="[
              parsedMarkdown === '' || isLoading
                ? '!cursor-not-allowed secondary-btn'
                : 'func-btn !bg-opacity-50 font-semibold ',
            ]"
            class="w-full"
            @click="onReGenerateAiModelAnswer"
            style=""
          >
            <div class="flex items-center justify-center">
              重新定制
              <span class="text-sm ms-2">
                [ 今日剩余
                <span class="text-red-500">
                  {{ remainingRequests }}
                </span>
                次]
              </span>
            </div>
          </button>
        </div>
        <button
          v-else
          class="primary-btn w-full !py-2"
          @click="router.push('/login')"
        >
          登录后体验定制功能
        </button>
      </div>
    </div>
  </div>
  <ConfirmModal ref="confirmModal"></ConfirmModal>
</template>

<script setup lang="ts">
import { ref, onUnmounted, computed, watch, onMounted } from "vue";
import MarkdownIt from "markdown-it";
import ConfirmModal from "@/components/ui/ConfirmModal.vue";
import LoadingBtn from "@/components/ui/LoadingBtn.vue";
import { useI18n } from "vue-i18n";
import type { conversationItem } from "@/types/topic/chat";
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
import { useExtractOralAnswer } from "@/hooks/useExtractOralAnswer";
import AiSpeaker from "./aiSpeaker.vue";
import { useToken } from "@/hooks/useToken";
import { Manager, Socket } from 'socket.io-client';
import toast from "@/utils/toaster";
import { useRouter } from 'vue-router';

/// <reference types="vite/client" />

const router = useRouter();
const { extractAnswerText } = useExtractOralAnswer();

const { isAuthenticated, getToken } = useToken();

const props = defineProps<{
  question: string;
  historyAnswer: string;
  userIdeas: string | null;
  partId: number;
  conversationContext: conversationItem[];
}>();

const emit = defineEmits<{
  (e: "reset"): void;
  (e: "post-answer", userIdeas: string, historyAnswer: string): void;
  (e: "re-generate-answer"): void;
}>();

const { t } = useI18n();
const md = new MarkdownIt();
const parsedMarkdown = computed(() => md.render(currentAnswer.value));
const plainAnswer = computed(() => extractAnswerText(currentAnswer.value));
const currentAnswer = ref("");
const isLoading = ref(false);
const userIdeas = ref("");
const aiAnswerPlaceHolder = ref<string>(
  "填入关键内容：关键词 + 拓展答案的关键词\n\n 问题：What's your favorite season?\n示例： 夏天（回答问题), 游泳, 吃冰淇淋"
);

const confirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null);
const remainingRequests = ref(0);
const isEditing = ref(false);

const socket = ref<typeof Socket | null>(null);
const isSocketConnected = ref(false);

const setupSocket = () => {
  if (isAuthenticated.value && !socket.value) {
    // Get socket URL from environment variable
    const socketUrl = (import.meta as any).env.VITE_SOCKET_URL;
    if (!socketUrl) {
      console.error('Socket URL not configured');
      return;
    }

    try {
      const manager = new Manager(socketUrl, {
        query: { token: getToken() },
        transports: ['websocket', 'polling'],
        path: '/socket.io'
      });

      const socketInstance = manager.socket('/');
      socket.value = socketInstance;

      socketInstance.on('connect', () => {
        isSocketConnected.value = true;
        console.log('Socket connected successfully');
      });

      socketInstance.on('connect_error', (error: Error) => {
        console.error('Socket connection error:', error);
        isSocketConnected.value = false;
        toast.error(`Socket连接失败: ${error.message}`);
      });

      socketInstance.on('disconnect', (reason: string) => {
        console.log('Socket disconnected:', reason);
        isSocketConnected.value = false;
        if (reason === 'io server disconnect') {
          socketInstance.connect();
        }
      });
    } catch (error) {
      console.error('Socket initialization error:', error);
      toast.error('Socket初始化失败，请刷新页面重试');
    }
  }
};

function setupSocketListeners() {
  if (!socket.value) return;

  // Remove existing listeners
  socket.value.off("answer");
  socket.value.off("stream_end");
  socket.value.off("error");
  socket.value.off("daily_limitation_warning");
  socket.value.off("remaining_requests");

  // Add new listeners
  socket.value.on("answer", (data: AnswerType) => {
    buffer += data.answer;
    if (isLoading.value) typewriterEffect();
  });

  socket.value.on("stream_end", () => {
    emit("post-answer", userIdeas.value, currentAnswer.value);
    isLoading.value = false;
  });

  socket.value.on("error", (message: string) => {
    toast.error("请求失败, 请重试");
    isLoading.value = false;
  });

  socket.value.on("daily_limitation_warning", (message: string) => {
    toast.error("今日免费次数已用尽,请升级套餐，或稍后重试！", {
      autoClose: 4000,
    });
    isLoading.value = false;
  });

  socket.value.on("remaining_requests", (data: { remainingRequests: number }) => {
    remainingRequests.value = data.remainingRequests;
    isLoading.value = false;
  });
}

const getRemainingSocketRequests = () => {
  socket.value?.emit("get_remaining_requests");
};

const onGenerateAiModelAnswer = () => {
  if (!socket.value?.connected) {
    setupSocket();
  }
  
  isLoading.value = true;
  buffer = "";
  currentAnswer.value = "";
  
  socket.value?.emit("get_answer", {
    question: props.question,
    userIdeas: userIdeas.value,
    partId: props.partId,
    conversationContext: props.conversationContext,
  });
};

const onReGenerateAiModelAnswer = () => {
  confirmModal.value
    ?.confirm(t("AI.reGenerateAnswerPrompt"))
    .then((isConfirmed) => {
      if (isConfirmed) {
        currentAnswer.value = "";
        emit("re-generate-answer");
      }
    });
};

const reset = () => {
  userIdeas.value = "";
  isLoading.value = false;
  buffer = "";
  currentAnswer.value = "";
};

const toggleEditMode = () => {
  isEditing.value = !isEditing.value;
  if (!isEditing.value) {
    emit("post-answer", userIdeas.value, currentAnswer.value);
  }
};

// socket stream logic
let buffer = "";
function typewriterEffect() {
  if (buffer.length > 0) {
    currentAnswer.value += buffer.charAt(0);
    buffer = buffer.slice(1);
    setTimeout(typewriterEffect, 50);
  }
}

interface AnswerType {
  answer: string;
}

onMounted(() => {
  setupSocket();
  setupSocketListeners();
  getRemainingSocketRequests();
});

watch(
  () => props.historyAnswer,
  (newValue) => {
    currentAnswer.value = newValue;
  },
  { immediate: true }
);

watch(
  () => props.userIdeas,
  () => {
    userIdeas.value = props.userIdeas ?? "";
  }
);

onUnmounted(() => {
  if (socket.value) {
    socket.value.off("answer");
    socket.value.off("stream_end");
    socket.value.off("error");
    socket.value.off("daily_limitation_warning");
    socket.value.off("remaining_requests");
    socket.value.disconnect();
  }
});

defineExpose({
  reset,
  currentAnswer,
});
</script>

<style scoped>
.markdown-content {
  max-height: 30rem;
  overflow-y: auto;
}

.markdown-content::-webkit-scrollbar {
  width: 6px;
}

.markdown-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.markdown-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.markdown-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
